import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import { ApiResponseSchema } from '../schemas';

export const GenderRatioSchema = z.object({
	male: z.number(),
	female: z.number()
}).strip();

export const DashboardKPIsSchema = z.object({
	totalStudents: z.number(),
	maleStudents: z.number(),
	femaleStudents: z.number(),
	genderRatio: GenderRatioSchema,
	averageAdmissionScore: z.number().nullable(),
	studentsWithGrade12Score: z.number(),
	grade12ScorePercentage: z.number(),
	totalProvinces: z.number(),
	admissionYears: z.array(z.number()),
	latestYear: z.number().nullable(),
	totalQuota: z.number(),
	enrollmentCount: z.number(),
	enrollmentPercentage: z.number(),
	top149EnrollmentCount: z.number(),
	top149Percentage: z.number()
}).strip();

export const MajorStatsSchema = z.object({
	majorCode: z.string(),
	majorName: z.string(),
	studentCount: z.number(),
	averageScore: z.number().nullable()
}).strip();

export const FacultyMajorStatsSchema = z.object({
	facultyCode: z.string(),
	facultyName: z.string(),
	studentCount: z.number(),
	averageScore: z.number().nullable(),
	genderRatio: GenderRatioSchema,
	majors: z.array(MajorStatsSchema)
}).strip();

export const AdmissionMethodStatsSchema = z.object({
	methodCode: z.string(),
	methodName: z.string(),
	studentCount: z.number(),
	averageScore: z.number().nullable(),
	percentage: z.number()
}).strip();

export const ProvinceStatsSchema = z.object({
	provinceCode: z.string(),
	provinceName: z.string(),
	studentCount: z.number(),
	percentage: z.number()
}).strip();

export const GeographicStatsSchema = z.object({
	totalProvinces: z.number(),
	provinceDistribution: z.array(ProvinceStatsSchema),
	top10Provinces: z.array(ProvinceStatsSchema.omit({ percentage: true })).default([])
}).strip();

export const GeographicStatsMergedSchema = z.object({
	totalProvinces: z.number(),
	provinceDistribution: z.array(ProvinceStatsSchema),
	top10ProvincesMerged: z.array(ProvinceStatsSchema.omit({ percentage: true })).default([])
}).strip();

export const ScoreRangeSchema = z.object({
	range: z.string(),
	count: z.number(),
	percentage: z.number()
}).strip();

export const Grade12ScoreAnalysisSchema = z.object({
	totalStudentsWithScores: z.number(),
	averageScores: z.object({
		dtb12: z.number().nullable(),
		toan: z.number().nullable(),
		van: z.number().nullable(),
		li: z.number().nullable(),
		hoa: z.number().nullable(),
		sinh: z.number().nullable(),
		su: z.number().nullable(),
		dia: z.number().nullable()
	}).strip(),
	scoreDistribution: z.array(z.object({
		subject: z.string(),
		ranges: z.array(ScoreRangeSchema)
	}).strip())
}).strip();

export const FilterOptionsSchema = z.object({
	years: z.array(z.number()),
	faculties: z.array(z.object({
		code: z.string(),
		name: z.string()
	}).strip()),
	admissionMethods: z.array(z.object({
		code: z.string(),
		name: z.string()
	}).strip())
}).strip();

export const GenderDistributionByFacultySchema = z.object({
	facultyCode: z.string(),
	facultyName: z.string(),
	maleCount: z.number(),
	femaleCount: z.number(),
	totalCount: z.number(),
	malePercentage: z.number(),
	femalePercentage: z.number()
}).strip();

export const GenderDistributionByMajorSchema = z.object({
	majorCode: z.string(),
	majorName: z.string(),
	maleCount: z.number(),
	femaleCount: z.number(),
	totalCount: z.number(),
	malePercentage: z.number(),
	femalePercentage: z.number()
}).strip();

export const TopSchoolsByMethodSchema = z.object({
	methodCode: z.string(),
	methodName: z.string(),
	schools: z.array(z.object({
		schoolCode: z.string(),
		schoolName: z.string(),
		studentCount: z.number(),
		averageScore: z.number().nullable(),
		provinceName: z.string()
	}).strip())
}).strip();

export type TopSchoolsByMethod = z.infer<typeof TopSchoolsByMethodSchema>;

export const HeatmapCellSchema = z.object({
	id: z.string(),
	data: z.array(z.object({
		x: z.string(),
		y: z.number()
	}).strip())
}).strip();

export const ProvinceByFacultyHeatmapSchema = z.object({
	data: z.array(HeatmapCellSchema)
}).strip();

export const ProvinceByMajorHeatmapSchema = z.object({
	data: z.array(HeatmapCellSchema)
}).strip();

export const RemainingQuotaStatsSchema = z.object({
	code: z.string(),
	name: z.string(),
	totalQuota: z.number(),
	enrollmentCount: z.number(),
	remainingQuota: z.number()
}).strip();

const TopSchoolsArraySchema = TopSchoolsByMethodSchema.shape.schools;

/**
 * Build a dashboard API URL from individual filter parameters.
 * Converts optional key-value pairs into URLSearchParams, then delegates to buildApiUrl.
 */
function buildDashboardUrl(
	path: string,
	params: Record<string, string | number | undefined>,
): string {
	const searchParams = new URLSearchParams();
	for (const [key, val] of Object.entries(params)) {
		if (val !== undefined) searchParams.set(key, String(val));
	}
	return buildApiUrl(`/dashboard${path}`, searchParams.size > 0 ? searchParams : undefined);
}

export const dashboardApi = {
	getKPIs: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/kpis', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(DashboardKPIsSchema).parse(response.data).data;
	},

	getFacultyMajorStats: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/faculty-major', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(FacultyMajorStatsSchema.array()).parse(response.data).data;
	},

	getAdmissionMethodStats: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/admission-methods', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(AdmissionMethodStatsSchema.array()).parse(response.data).data;
	},

	getGeographicStats: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/geographic', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(GeographicStatsSchema).parse(response.data).data;
	},

	getGeographicStatsMerged: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/geographic-merged', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(GeographicStatsMergedSchema).parse(response.data).data;
	},

	getGrade12ScoreAnalysis: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/grade12-scores', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(Grade12ScoreAnalysisSchema).parse(response.data).data;
	},

	getFilterOptions: async () => {
		const url = buildDashboardUrl('/filters', {});
		const response = await apiClient.get(url);
		return ApiResponseSchema(FilterOptionsSchema).parse(response.data).data;
	},

	getGenderDistributionByFaculty: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/gender-by-faculty', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(GenderDistributionByFacultySchema.array()).parse(response.data).data;
	},

	getTopSchoolsByMethod: async (year?: number, methodCode?: string, maKhoa?: string) => {
		const url = buildDashboardUrl('/top-schools-by-method', { year, methodCode, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(TopSchoolsByMethodSchema.array()).parse(response.data).data;
	},

	getGenderDistributionByMajor: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/gender-by-major', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(GenderDistributionByMajorSchema.array()).parse(response.data).data;
	},

	getMajorStatsByFaculty: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/major-stats', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(MajorStatsSchema.array()).parse(response.data).data;
	},

	getProvinceByFacultyHeatmap: async (year?: number) => {
		const url = buildDashboardUrl('/heatmap-province-faculty', { year });
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceByFacultyHeatmapSchema).parse(response.data).data;
	},

	getProvinceByFacultyHeatmapMerged: async (year?: number) => {
		const url = buildDashboardUrl('/heatmap-province-faculty-merged', { year });
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceByFacultyHeatmapSchema).parse(response.data).data;
	},

	getProvinceByMajorHeatmap: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/heatmap-province-major', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceByMajorHeatmapSchema).parse(response.data).data;
	},

	getProvinceByMajorHeatmapMerged: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/heatmap-province-major-merged', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(ProvinceByMajorHeatmapSchema).parse(response.data).data;
	},

	getRemainingQuotaStats: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/remaining-quota', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(RemainingQuotaStatsSchema.array()).parse(response.data).data;
	},

	getTopSchools: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/top-schools', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(TopSchoolsArraySchema).parse(response.data).data;
	},

	getTopSchoolsTop149: async (year?: number, maKhoa?: string) => {
		const url = buildDashboardUrl('/top-schools-top149', { year, maKhoa });
		const response = await apiClient.get(url);
		return ApiResponseSchema(TopSchoolsArraySchema).parse(response.data).data;
	},

	hasData: async (): Promise<boolean> => {
		const url = buildDashboardUrl('/has-data', {});
		const response = await apiClient.get(url);
		return ApiResponseSchema(z.object({ hasData: z.boolean() })).parse(response.data).data.hasData;
	},
};
